// Produzione — kit UI touch-first (tema antracite / blu acciaio)
import React from "react";
import { fmtKg } from "./prodApi";

const BTN = {
  primary: "bg-blue-600 hover:bg-blue-500 text-white border-blue-400/30",
  success: "bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-400/30",
  ghost: "bg-slate-800 hover:bg-slate-700 text-slate-100 border-slate-700",
  danger: "bg-slate-900 hover:bg-red-950 text-red-300 border-red-500/40",
  amber: "bg-amber-500 hover:bg-amber-400 text-slate-950 border-amber-300/40",
};
const SIZES = { sm: "h-11 px-4 text-sm", md: "h-14 px-5 text-base", lg: "h-16 px-6 text-lg" };

export const PButton = ({ variant = "ghost", size = "md", className = "", children, ...props }) => (
  <button
    className={`${SIZES[size]} ${BTN[variant]} font-bold rounded-xl border flex items-center justify-center gap-2 select-none transition-[background-color,transform] active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none ${className}`}
    {...props}
  >
    {children}
  </button>
);

const INPUT = "w-full bg-slate-900 border-2 border-slate-700 focus:border-blue-500 focus:outline-none text-white rounded-xl";
export const PInput = React.forwardRef(({ className = "", ...props }, ref) => (
  <input ref={ref} className={`${INPUT} h-14 px-4 text-lg font-medium placeholder:text-slate-500 ${className}`} {...props} />
));
export const PNum = React.forwardRef(({ className = "", ...props }, ref) => (
  <input ref={ref} type="text" inputMode="decimal" autoComplete="off" className={`${INPUT} h-14 px-4 text-xl font-['Chivo'] font-bold tabular-nums text-right placeholder:text-slate-600 ${className}`} {...props} />
));
export const PSelect = ({ className = "", children, ...props }) => (
  <select className={`${INPUT} h-14 px-3 text-base font-medium ${className}`} {...props}>{children}</select>
);
export const PTextarea = ({ className = "", ...props }) => (
  <textarea className={`${INPUT} min-h-[96px] p-4 text-base ${className}`} {...props} />
);

export const PField = ({ label, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
  </label>
);

export const PCard = ({ className = "", children, ...props }) => (
  <div className={`bg-slate-800/60 border border-slate-700 rounded-2xl p-4 md:p-5 ${className}`} {...props}>{children}</div>
);

const TONES = {
  slate: "bg-slate-700/60 text-slate-200 border-slate-600",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/50",
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/50",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/50",
  red: "bg-red-500/15 text-red-300 border-red-500/50",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/50",
};
export const PBadge = ({ tone = "slate", className = "", children, ...props }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide border ${TONES[tone]} ${className}`} {...props}>{children}</span>
);

export const PTitle = ({ kicker, title, right, children }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
    <div>
      {kicker && <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400">{kicker}</p>}
      <h1 className="font-['Chivo'] font-black text-2xl sm:text-3xl text-white tracking-tight">{title}</h1>
      {children && <p className="text-slate-400 text-sm mt-1">{children}</p>}
    </div>
    {right && <div className="flex flex-wrap gap-2">{right}</div>}
  </div>
);

export const PModal = ({ open, onClose, title, children, wide = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start md:items-center justify-center p-2 md:p-6 overflow-y-auto" onClick={onClose}>
      <div className={`bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full ${wide ? "max-w-4xl" : "max-w-xl"} my-4`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-800">
          <h2 className="font-['Chivo'] font-black text-lg md:text-xl text-white">{title}</h2>
          <button onClick={onClose} className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl text-slate-300 hover:bg-slate-800" data-testid="pmodal-close">×</button>
        </div>
        <div className="p-4 md:p-6">{children}</div>
      </div>
    </div>
  );
};

export const PToggle = ({ options, value, onChange, testIdPrefix = "toggle", className = "" }) => (
  <div className={`flex bg-slate-900 border-2 border-slate-700 rounded-xl p-1 gap-1 ${className}`}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        data-testid={`${testIdPrefix}-${o.value}`}
        className={`h-12 flex-1 px-3 rounded-lg font-bold text-sm transition-colors ${value === o.value ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const KPI = ({ label, value, sub, tone = "slate", testId }) => {
  const color = { slate: "text-white", green: "text-emerald-400", amber: "text-amber-400", red: "text-red-400", blue: "text-blue-400" }[tone];
  return (
    <PCard className="!p-4" data-testid={testId}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`font-['Chivo'] font-black text-2xl sm:text-3xl tabular-nums mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </PCard>
  );
};

export const PEmpty = ({ title, text, action, testId }) => (
  <PCard className="text-center py-10" data-testid={testId}>
    <p className="font-['Chivo'] font-black text-xl text-white">{title}</p>
    {text && <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">{text}</p>}
    {action && <div className="mt-5 flex justify-center">{action}</div>}
  </PCard>
);

export function reconcileStatus(input, total) {
  if (!(input > 0)) return { status: "unknown", diff: 0, pct: 0 };
  const diff = input - total;
  const pct = Math.min(100, Math.max(0, (total / input) * 100));
  if (Math.abs(diff) < 0.005) return { status: "ok", diff: 0, pct: 100 };
  if (diff < 0) return { status: "excess", diff, pct: 100 };
  return { status: "pending", diff, pct };
}

const REC_STYLE = {
  unknown: { bar: "bg-slate-600", text: "text-slate-400", label: "In attesa del peso" },
  pending: { bar: "bg-amber-500", text: "text-amber-400", label: "Da riconciliare" },
  ok: { bar: "bg-emerald-500", text: "text-emerald-400", label: "Riconciliato 100%" },
  excess: { bar: "bg-red-500", text: "text-red-400", label: "Eccesso: output > input" },
};

export const ReconcileBar = ({ input, total, unit = "kg", testId = "reconcile-bar" }) => {
  const st = reconcileStatus(input, total);
  const s = REC_STYLE[st.status];
  return (
    <div data-testid={testId} data-status={st.status}>
      <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider">
        <span className={s.text}>{s.label}</span>
        <span className={`${s.text} tabular-nums`}>{st.status === "unknown" ? "—" : `${st.diff >= 0 ? "" : "+"}${fmtKg(Math.abs(st.diff) * (st.diff < 0 ? 1 : 1), unit === "%" ? 2 : 3)} ${unit} ${st.diff > 0 ? "mancanti" : st.diff < 0 ? "in eccesso" : ""}`}</span>
      </div>
      <div className="w-full h-4 bg-slate-800 rounded-full overflow-hidden border border-slate-700 mt-1.5">
        <div className={`h-full rounded-full transition-[width] duration-300 ease-out ${s.bar} ${st.status === "ok" ? "shadow-[0_0_16px_rgba(16,185,129,0.6)]" : ""}`} style={{ width: `${st.pct}%` }} />
      </div>
    </div>
  );
};

export const Spinner = ({ text = "Calcolo rese e costi in corso..." }) => (
  <div className="flex items-center gap-3 text-slate-400 text-sm py-6" data-testid="prod-spinner">
    <span className="w-5 h-5 border-2 border-slate-600 border-t-blue-500 rounded-full animate-spin" /> {text}
  </div>
);
